// Supporting long-tail guide pack for the two biggest hub pages on the site:
// /breeding (palworld breeding calculator, 40.5k/mo) and /map (palworld map,
// 33.1k/mo). Each guide owns one specific child query and links back up to its
// hub, so the hub collects internal-link equity from a cluster instead of
// standing alone against much larger sites.
//
// Content is generated from an explicit topic spec so every article carries
// real, topic-specific text (no boilerplate padding) and the site rule of
// exactly five articles per guide holds automatically.

import type { Guide, GuideArticle } from "./guides-data";
import { OFFICIAL_LINKS } from "./official-links";

interface Topic {
  slug: string;
  title: string;
  desc: string;
  keywords: string;
  /** Hub page this cluster feeds. */
  hub: string;
  hubLabel: string;
  /** The exact question the guide answers, used as the AEO answer heading. */
  question: string;
  /** Direct answer paragraph — kept under ~50 words for answer-box extraction. */
  answer: string;
  /** Ordered walkthrough steps. */
  steps: [string, string][];
  /** Reference rows: label + value/explanation. */
  data: [string, string][];
  /** Mistakes players actually make, plus the fix. */
  pitfalls: [string, string][];
  /** Four supporting Q&A pairs. */
  faq: [string, string][];
}

const TOPICS: Topic[] = [
  {
    slug: "palworld-breeding-calculator-all-combos",
    title: "Palworld Breeding Calculator: All Combos",
    desc: "How the Palworld breeding calculator resolves every parent pair, how breed power decides the child, and how to read the full combo list for any Pal.",
    keywords: "palworld breeding calculator all combos, palworld breeding combos list, palworld breed power chart, palworld parent pair calculator",
    hub: "/breeding",
    hubLabel: "Palworld breeding calculator",
    question: "How does the Palworld breeding calculator find every combo?",
    answer:
      "Every Pal has a hidden breed-power value. The calculator averages both parents' breed power, then picks the Pal whose own value sits closest to that average — so it can walk the whole roster and list every parent pair that lands on your target.",
    steps: [
      ["Pick the target first, not the parents", "Open the reverse lookup on the breeding calculator and choose the Pal you want. Working backwards from the target is always faster than testing pairs you happen to own."],
      ["Sort the returned pairs by what you already have", "Any listed pair produces the target. Choose the pair where you already own both species, or where both spawn in the same region so one trip catches both."],
      ["Check the child in the forward calculator", "Select your two chosen parents in the Parents to Child panel to confirm the result before you spend Cake — gender and level never change the species, so the forward check is definitive."],
      ["Chain the result if the pair is missing", "If neither parent is obtainable yet, run a second reverse lookup on the closer parent. Two short hops usually beat hunting one rare species."],
      ["Lock passives on the last generation", "Species and passives are decided separately. Get the species route working first, then re-breed the final pair with the passive pool you want."],
    ],
    data: [
      ["Species result", "Decided only by the two parents' breed power average — never by level, gender, condition or food."],
      ["Gender", "Only decides whether a pair can breed at all; a male and a female of any level works."],
      ["Cake", "One Cake per egg, consumed from the farm's box. Production stops the moment the box empties."],
      ["Passives", "Each child rolls from the combined passive pool of both parents, so stack traits on the parents first."],
      ["Egg type", "The egg's visual tier follows the child's element, which is why some routes always produce Scorching or Frozen eggs."],
    ],
    pitfalls: [
      ["Testing random pairs in-game", "Each failed attempt costs a Cake and several minutes. The reverse lookup removes the guesswork entirely."],
      ["Levelling parents before breeding", "Levels are wasted effort for species targeting. Level the child instead, after it hatches."],
      ["Breeding for species and passives at once", "Splitting the two goals across generations is faster than praying for both in one egg."],
      ["Letting the Cake box run dry overnight", "Assign a dedicated Cake production base so long AFK sessions actually produce eggs."],
    ],
    faq: [
      ["Does the Palworld breeding calculator list every possible combo?", "Yes. It evaluates the whole roster against the target's breed power, so every valid parent pair appears rather than just the popular ones."],
      ["Why do two different pairs give the same Pal?", "Because both pairs average to the same breed-power band. Any pair inside that band produces the same child."],
      ["Does parent level change the child in Palworld?", "No. Level, condition and food have no effect on the resulting species — only the two species matter."],
      ["Can I breed a Pal I have never caught?", "Yes, as long as a valid parent pair exists. That is the main use of the reverse lookup: reaching Pals you have not found in the wild."],
    ],
  },
  {
    slug: "palworld-breeding-passive-skill-odds",
    title: "Palworld Breeding Passive Skill Odds",
    desc: "How passive traits pass from parents to child in Palworld, the practical odds per generation, and the chain order that reaches a four-passive Pal fastest.",
    keywords: "palworld breeding passive skill odds, palworld passive inheritance chance, palworld legend musclehead breeding, palworld four passive pal",
    hub: "/breeding",
    hubLabel: "Palworld breeding calculator",
    question: "How do passive skills pass down when breeding in Palworld?",
    answer:
      "A child rolls its passives from the combined pool of both parents, then may add a random trait. Fewer junk passives on the parents means a higher chance the child inherits the ones you actually want, so cull bad traits every generation.",
    steps: [
      ["Decide the final four traits first", "Pick the exact set — for a combat Pal that is usually Legend, Musclehead, Ferocious and Swift — before you breed anything."],
      ["Clean the parent pool", "Breed out parents carrying filler traits. A parent with one wanted trait and one empty slot beats a parent with four traits where three are junk."],
      ["Bank a two-trait child", "Hatch until a child carries two of the four target traits, then retire the original parents."],
      ["Pair the bank against a fresh carrier", "Breed that child with a partner carrying the other two traits. This is the generation where most players succeed."],
      ["Re-roll only the final egg", "If the last child lands three of four, keep re-breeding that exact pair rather than restarting the chain."],
    ],
    data: [
      ["Pool source", "Both parents' passives are merged into one candidate pool per egg."],
      ["Slots", "A Pal holds up to four passives; extra rolls simply do not land."],
      ["Random additions", "A child can gain a trait neither parent had, which is why culling matters more than adding."],
      ["Species independence", "Passive inheritance is unaffected by the species route, so plan them separately."],
      ["Cost per attempt", "One Cake per egg, plus incubation time — the reason chain order matters more than luck."],
    ],
    pitfalls: [
      ["Keeping four-passive parents with junk traits", "They dilute the pool. A cleaner two-trait parent produces the target set faster."],
      ["Re-rolling from the first generation", "Always breed forward from your best child; restarting throws away banked progress."],
      ["Ignoring species while chasing passives", "Confirm the pair still produces the species you want in the calculator before committing."],
      ["Farming without a Cake surplus", "Passive chains need dozens of eggs. Build the Cake base before the chain, not during it."],
    ],
    faq: [
      ["What are the best passives to breed in Palworld?", "For combat: Legend, Musclehead, Ferocious and Swift. For base work: Artisan, Serious, Lucky and Work Slave equivalents that raise work speed."],
      ["Can a child get a passive neither parent has?", "Yes. Random traits can appear, which is why cutting junk traits from parents raises your effective odds."],
      ["Do passives transfer through several generations?", "Yes. Each child becomes a carrier, so banking progress generation by generation is the fastest route to four traits."],
      ["Does the species route change passive odds?", "No. Species comes from breed power and passives come from the parent pool; the two systems are independent."],
    ],
  },
  {
    slug: "palworld-breeding-cake-farm-setup",
    title: "Palworld Cake Farm Setup for Breeding",
    desc: "The base layout that keeps a Palworld breeding farm producing eggs overnight: cake ingredients, worker suitabilities and throughput per hour.",
    keywords: "palworld cake farm setup, palworld cake ingredients breeding, palworld breeding farm base layout, palworld cake production per hour",
    hub: "/breeding",
    hubLabel: "Palworld breeding calculator",
    question: "What do you need to keep a Palworld breeding farm running?",
    answer:
      "Cake is the bottleneck: each egg costs one Cake, and one Cake needs flour, milk, eggs, honey and berries. A dedicated base with wheat, a Berry Plantation, a Mill, a Ranch and Cooking Pot workers keeps eggs hatching without you.",
    steps: [
      ["Split breeding off into its own base", "Combat and ore bases compete for workers. A dedicated Cake base keeps ingredient production uninterrupted."],
      ["Plant wheat and berries together", "Wheat feeds the Mill for flour; berries cover both Cake and Pal food, so the base never starves itself."],
      ["Add a Ranch for milk, eggs and honey", "Assign Pals whose suitability produces the ranch drops you need instead of hauling them in by hand."],
      ["Staff the Cooking Pot with Kindling and Handiwork", "Cake only appears while a cook is assigned; Kindling keeps the pot hot and Handiwork raises throughput."],
      ["Feed the Breeding Farm box, not your inventory", "Cake must sit in the farm's own box. Assign a Transporting Pal so finished Cake moves there automatically."],
    ],
    data: [
      ["Cake per egg", "One, consumed from the Breeding Farm box."],
      ["Ingredients", "Flour, Red Berries, Milk, Egg and Honey — every one of them farmable in a single base."],
      ["Bottleneck", "Flour. The Mill is slower than the Plantation, so build a second Mill before a second Pot."],
      ["Worker priority", "Kindling and Handiwork at the pot, Gathering and Planting on crops, Transporting to move stock."],
      ["Overnight throughput", "A fully staffed base keeps a Breeding Farm supplied for a whole AFK session instead of a few eggs."],
    ],
    pitfalls: [
      ["No Transporting Pal", "Cake sits in the Pot and the farm goes idle. One Transporting worker fixes the whole loop."],
      ["Berry-only diet", "Workers lose SAN and stop producing. Keep cooked food and beds in the same base."],
      ["One Mill for two Pots", "Flour starves the pots. Scale Mills first, Pots second."],
      ["Breeding in a combat base", "Alpha aggro and raid events interrupt production; keep breeding somewhere quiet."],
    ],
    faq: [
      ["How much Cake do I need for a breeding chain?", "Budget one Cake per egg and dozens of eggs per passive chain, which is why a dedicated Cake base beats hand-crafting."],
      ["Does Cake quality change the egg?", "No. Cake only enables breeding; the child species comes from the parents' breed power."],
      ["Can I run two Breeding Farms from one Cake base?", "Yes, if you scale flour first — two Mills and two Pots comfortably feed two farms."],
      ["Do the parents need to be fed while breeding?", "Yes. Keep a stocked Feed Box in the base or production stalls when the parents get hungry."],
    ],
  },
  {
    slug: "palworld-legendary-breeding-combos",
    title: "Palworld Legendary Breeding Combos",
    desc: "Parent pairs for Palworld's legendary and high-value Pals, how to reach them without owning either parent, and how to keep the passives you bred for.",
    keywords: "palworld legendary breeding combos, how to breed anubis palworld, palworld jetragon breeding, palworld best breeding combinations legendary",
    hub: "/breeding",
    hubLabel: "Palworld breeding calculator",
    question: "How do you breed legendary Pals in Palworld?",
    answer:
      "Legendaries follow the same breed-power rule as everything else: find any parent pair whose average lands on the legendary's value. Use the reverse lookup, pick the pair you can actually catch, then chain intermediate children if you own neither parent.",
    steps: [
      ["Reverse-look-up the legendary", "Select the legendary as the target on the breeding calculator to see every valid pair rather than the one combo a video mentioned."],
      ["Score each pair by catchability", "Prefer pairs whose parents spawn in regions you have already unlocked; rarity of the parent matters more than its stats."],
      ["Bridge with an intermediate child", "If both parents are out of reach, breed one of them first. Chaining two easy generations is faster than hunting a single rare spawn."],
      ["Breed the species before the passives", "Get one copy of the legendary, then use it as a parent to fold in Legend, Musclehead, Ferocious and Swift."],
      ["Keep a spare breeding pair", "Retain both parents after success — re-rolling passives on a legendary needs repeat eggs."],
    ],
    data: [
      ["Rule", "Legendaries have no special breeding restriction; only their breed-power value is unusual."],
      ["Pair count", "Most legendaries have several valid pairs, so the reverse lookup usually offers a route you can afford."],
      ["Passives", "Inherited from the parent pool exactly like any other Pal — plan the chain after the first copy hatches."],
      ["Time cost", "Legendary eggs use the same Cake and incubation cost as commons; only the parent hunt is longer."],
      ["Stat rolls", "Individual stat rolls vary per egg, so repeat eggs from the same pair are still worth hatching."],
    ],
    pitfalls: [
      ["Chasing one memorised combo", "Guides often quote a single pair. The calculator usually shows an easier one for your save."],
      ["Stopping at the first hatch", "The first legendary rarely has the passive set you want; keep the parents and re-roll."],
      ["Mixing subspecies targets", "Confirm you selected the exact species — subspecies have their own breed-power values."],
      ["Under-resourcing Cake", "Legendary passive chains eat the most Cake of any route; scale the farm first."],
    ],
    faq: [
      ["Can every legendary be bred in Palworld?", "Any Pal with a valid parent pair in the reverse lookup can be bred; the calculator shows immediately whether a route exists."],
      ["Is breeding faster than catching a legendary?", "Usually yes once your Cake base runs, because breeding is repeatable and catching depends on spawn timers."],
      ["Do bred legendaries have worse stats?", "No. Stat rolls are independent of how the Pal was obtained, and repeat eggs let you keep the best roll."],
      ["What is the fastest legendary route for a new save?", "Pick the legendary whose reverse lookup lists parents from your unlocked regions, then chain from there."],
    ],
  },
  {
    slug: "palworld-egg-hatch-times-incubator",
    title: "Palworld Egg Hatch Times & Incubators",
    desc: "How Palworld incubation works, what changes hatch speed, and how to run several incubators so a breeding chain never waits on a single egg.",
    keywords: "palworld egg hatch times, palworld incubator temperature, palworld how long to hatch eggs, palworld multiple incubators setup",
    hub: "/breeding",
    hubLabel: "Palworld breeding calculator",
    question: "How long do Palworld eggs take to hatch?",
    answer:
      "Hatch time depends on the egg's tier and your incubator's temperature comfort. Keeping an incubator at its comfortable temperature with heaters or coolers nearby is what actually shortens the wait — not the parents you used.",
    steps: [
      ["Build the Egg Incubator inside the breeding base", "Short walks between farm and incubator matter once you are hatching dozens of eggs."],
      ["Fix the temperature first", "Add a Heater or Cooler beside the incubator so the comfort indicator stays satisfied through day and night cycles."],
      ["Run several incubators in parallel", "A breeding chain produces eggs faster than one incubator clears them; three or more keeps the pipeline moving."],
      ["Batch-collect on a timer", "Return between sessions rather than watching a single egg — the farm keeps laying while you are away."],
      ["Sort eggs by target", "Label chests by chain so species eggs and passive-chain eggs do not get mixed up."],
    ],
    data: [
      ["Driver of hatch time", "Egg tier plus incubator comfort; higher-tier eggs always take longer."],
      ["Temperature", "Comfortable incubators hatch on schedule; uncomfortable ones stall progress."],
      ["Parallelism", "Incubators work independently, so throughput scales linearly with how many you build."],
      ["Egg element", "The egg's element follows the child, which is a useful preview of the route working."],
      ["Server settings", "Multiplayer worlds can scale hatch duration, so private-server times differ from single player."],
    ],
    pitfalls: [
      ["One incubator for a whole chain", "The farm outruns it immediately; build more before starting a passive chain."],
      ["Ignoring the comfort indicator", "An uncomfortable incubator quietly wastes hours of real time."],
      ["Incubating far from the farm", "Travel time adds up over dozens of eggs; keep both in one base."],
      ["Mixing chains in one chest", "Losing track of which egg belongs to which route wastes whole generations."],
    ],
    faq: [
      ["Does incubator temperature really change hatch speed in Palworld?", "Yes. Keeping the incubator inside its comfortable range is the main lever you control over hatch duration."],
      ["Can I hatch several Palworld eggs at once?", "Yes — each Egg Incubator works independently, so building several is the standard fix for a slow breeding pipeline."],
      ["Do eggs hatch while I am offline?", "In single player, progress follows in-game time, so plan long chains around active sessions or server settings."],
      ["Does the egg's element tell me the child?", "It narrows it down: the egg tier follows the child's element, which confirms your route is producing the right family."],
    ],
  },
  {
    slug: "palworld-map-dungeon-locations",
    title: "Palworld Map Dungeon Locations",
    desc: "Every Palworld dungeon type by region and level band, what each one drops, and the route order that keeps dungeon runs profitable.",
    keywords: "palworld map dungeon locations, palworld dungeon list by level, palworld dungeon drops, palworld best dungeon farming route",
    hub: "/map",
    hubLabel: "Palworld map",
    question: "Where are the dungeons on the Palworld map?",
    answer:
      "Dungeon entrances sit in fixed spots across every region and scale with that region's level band. Filter the interactive Palworld map by dungeon and level, then run the band just under your squad's level for the best clear speed per reward.",
    steps: [
      ["Filter the map by dungeon and level band", "Running a band above your squad wastes potions; one band below clears fastest per minute."],
      ["Unlock the nearest fast travel statue first", "Dungeon runs are a loop — a statue within sprint distance roughly doubles runs per hour."],
      ["Bring the counter element", "Each dungeon's interior favours one or two elements; the damage calculator shows which squad answers it."],
      ["Clear to the boss room, then reset", "The end-room chest and boss carry the run's value; skip optional side rooms once you know the layout."],
      ["Rotate entrances", "Cleared dungeons re-roll on a timer, so keep three entrances in rotation instead of camping one."],
    ],
    data: [
      ["Entrance placement", "Fixed per region on the map, with interiors drawn from a pool of layouts."],
      ["Level bands", "Interiors scale to the region, so a Sakurajima dungeon is far above a Windswept Hills one."],
      ["Rewards", "Ingots, schematics, spheres and boss drops, weighted by the band."],
      ["Reset", "Entrances re-roll on a timer; rotating between three keeps downtime near zero."],
      ["Element bias", "Interiors favour specific elements — check before you commit the squad."],
    ],
    pitfalls: [
      ["Running a band above your squad", "Longer fights and more potions for the same chest; drop one band and clear twice as fast."],
      ["No fast travel nearby", "Travel dominates the loop; unlock the statue before farming the dungeon."],
      ["One-element squad", "A mono-element team stalls on the wrong interior; bring two coverage picks."],
      ["Camping a single entrance", "Reset timers idle you out. Keep a rotation of three."],
    ],
    faq: [
      ["How many dungeon types are on the Palworld map?", "Each region hosts dungeon entrances drawn from a shared layout pool, so the interiors repeat while the level band and rewards follow the region."],
      ["What is the best dungeon farming route in Palworld?", "Chain three entrances that share one fast travel statue and run them in order, so reset timers finish before you return."],
      ["Do Palworld dungeons reset?", "Yes, entrances re-roll on a timer, which is why a rotation beats camping one location."],
      ["What level should I be for late-game dungeons?", "Aim for the top of the region's band or just above it; the interiors scale with the region, not with you."],
    ],
  },
  {
    slug: "palworld-map-fast-travel-statues",
    title: "Palworld Map Fast Travel Statues",
    desc: "Where every Palworld fast travel point and statue sits, the unlock order that saves the most travel time, and how statues change your base placement.",
    keywords: "palworld map fast travel statues, palworld teleport locations, palworld statue of power locations, palworld fast travel unlock order",
    hub: "/map",
    hubLabel: "Palworld map",
    question: "Where are the fast travel points on the Palworld map?",
    answer:
      "Fast travel points sit at named landmarks and statues across every region and stay unlocked once touched. Unlock the ones nearest your ore, cake and boss loops first — travel time, not combat, is what slows most saves down.",
    steps: [
      ["Touch every statue you pass", "Unlocks are permanent and free; detouring 20 seconds now saves minutes every session later."],
      ["Anchor bases beside a statue", "A base within sprint range of a travel point turns hauling loops into one-click trips."],
      ["Unlock along your farming loop first", "Ore, cake ingredients and boss arenas deserve statues before scenic regions do."],
      ["Bridge to the next region early", "One statue at a region border makes the whole next band accessible before you are levelled for it."],
      ["Re-plan after each new region", "New statues often shorten an old loop; recheck the map when a region opens."],
    ],
    data: [
      ["Persistence", "Once activated, a travel point stays available for the save."],
      ["Placement", "Named landmarks, towers and statues — all visible on the interactive map."],
      ["Cost", "Free to use; the only cost is the first visit on foot or by mount."],
      ["Base synergy", "Bases beside statues cut hauling loops dramatically."],
      ["Region bridging", "Border statues open higher bands before your level catches up."],
    ],
    pitfalls: [
      ["Skipping statues while questing", "Backtracking later costs far more than the original detour."],
      ["Placing bases far from travel points", "Every resource run becomes a manual flight."],
      ["Only unlocking your home region", "Boss and ore loops in other regions stay expensive to reach."],
      ["Forgetting the mount comparison", "A faster flier plus fewer statues can beat a slow mount with many; check the mount ranking."],
    ],
    faq: [
      ["Do Palworld fast travel points stay unlocked?", "Yes. Activating a statue or landmark unlocks it permanently for that save."],
      ["Can I fast travel with a full inventory in Palworld?", "Travel restrictions apply to some cargo, so plan hauling routes with a mount and a base near the statue."],
      ["Which fast travel points should I unlock first?", "The ones on your ore, cake and boss loops — those are the routes you repeat every session."],
      ["Should I build bases next to statues?", "Yes, it is the single biggest quality-of-life gain on the map because it removes repeat travel entirely."],
    ],
  },
  {
    slug: "palworld-map-alpha-boss-locations",
    title: "Palworld Map Alpha Boss Locations",
    desc: "Every Palworld field alpha and tower boss by region and level, what each one drops, and the counter elements that shorten the fight.",
    keywords: "palworld map alpha boss locations, palworld field alpha list, palworld tower boss locations, palworld alpha pal respawn",
    hub: "/map",
    hubLabel: "Palworld map",
    question: "Where are the alpha bosses on the Palworld map?",
    answer:
      "Field alphas sit at fixed points in each region and respawn on a timer, while tower bosses hold five dedicated arenas. Filter the map by boss and level, then bring the counter element from the damage calculator before you commit.",
    steps: [
      ["Filter the map by boss and level", "Fight the alphas inside your band first; the reward curve does not justify overreaching."],
      ["Look up the counter element", "Element multipliers matter more than raw level in alpha fights — the damage calculator gives the exact multiplier."],
      ["Set up a statue and a base nearby", "Respawn farming is a loop; travel time decides how many attempts per hour you get."],
      ["Bring capture spheres, not just weapons", "Alphas can be caught, and a captured alpha becomes a strong mount or base worker."],
      ["Farm the respawn window", "Note the clear time and return when the timer lapses instead of wandering between spawns."],
    ],
    data: [
      ["Field alphas", "Fixed map locations per region, scaled to the region's level band, respawning on a timer."],
      ["Tower bosses", "Five arena fights that gate progression rather than farming."],
      ["Drops", "Schematics, ingots, ancient parts and species-specific materials."],
      ["Capture", "Field alphas can be captured, which is often better value than killing them."],
      ["Counters", "Element multipliers shorten fights more than levels do."],
    ],
    pitfalls: [
      ["Fighting without a type counter", "Neutral matchups turn a two-minute alpha into a ten-minute potion drain."],
      ["No nearby travel point", "Respawn farming collapses when each attempt needs a five-minute flight."],
      ["Killing every alpha", "Capturing gives you the Pal as a mount or worker on top of the drops."],
      ["Ignoring tower gating", "Tower bosses unlock progression; slot them between farming loops rather than last."],
    ],
    faq: [
      ["Do alpha Pals respawn in Palworld?", "Field alphas respawn on a timer at their fixed map locations, so they can be farmed; tower bosses are progression fights."],
      ["Can you catch alpha Pals?", "Yes. Field alphas can be captured with high-tier spheres, which usually beats killing them for drops alone."],
      ["What level should I fight Palworld alphas at?", "Match the region's band and bring the counter element; the multiplier matters more than a few levels."],
      ["How many tower bosses are in Palworld?", "Five tower arenas gate progression, separate from the field alphas scattered across the map."],
    ],
  },
  {
    slug: "palworld-map-ore-farming-locations",
    title: "Palworld Map Ore Farming Locations",
    desc: "The best ore, coal, sulfur and quartz nodes on the Palworld map, plus the base layout that turns a node cluster into passive ingot production.",
    keywords: "palworld map ore farming locations, palworld best ore base locations, palworld coal and sulfur locations, palworld pure quartz farming",
    hub: "/map",
    hubLabel: "Palworld map",
    question: "Where are the best ore locations on the Palworld map?",
    answer:
      "The best ore spots are node clusters big enough for a base radius to cover several nodes at once. Filter the map for ore, coal, sulfur and quartz, then place the Palbox so mining Pals work the whole cluster without you.",
    steps: [
      ["Find a cluster, not a single node", "One node is a pickaxe trip; four inside a base radius is passive ingot income."],
      ["Place the Palbox to cover the nodes", "Rotate placement until the build radius touches as many nodes as possible before you commit."],
      ["Staff with Mining and Transporting", "Miners break the nodes and transporters move ore to the furnace loop; missing either stalls the base."],
      ["Add furnaces and a Kindling worker", "Raw ore is dead weight; converting to ingots on site removes a whole hauling trip."],
      ["Unlock the nearest travel point", "Ingot runs repeat forever, so a statue nearby pays back immediately."],
    ],
    data: [
      ["Ore", "The core ingot input; cluster sites matter more than individual node counts."],
      ["Coal", "Needed for refined ingots — pair a coal base with your ore base rather than mixing crops in."],
      ["Sulfur", "Ammunition and gunpowder input, concentrated in volcanic and desert bands."],
      ["Pure Quartz", "Late-game circuit material found in the higher-level regions such as the Sakurajima band."],
      ["Throughput", "Set by miner count and furnace count, not by the node respawn rate."],
    ],
    pitfalls: [
      ["Base radius clipping one node", "Reposition the Palbox; a single covered node barely beats manual mining."],
      ["No Transporting Pal", "Ore piles up at the node and mining halts."],
      ["Mixing crops into the ore base", "Workers split across jobs and both loops slow down; keep bases specialised."],
      ["Hauling raw ore home", "Smelt on site and carry ingots instead."],
    ],
    faq: [
      ["What is the best ore base location in Palworld?", "Any spot where the Palbox radius covers several ore nodes at once — cluster coverage beats a scenic location every time."],
      ["Where do you find Pure Quartz in Palworld?", "In the higher-level late-game regions; filter the interactive map for quartz and route a farming loop through the densest cluster."],
      ["How many mining Pals do I need?", "Enough miners to keep every covered node busy plus at least one transporter, then scale furnaces to match."],
      ["Should I smelt ore at the mining base?", "Yes. Smelting on site removes the heaviest hauling step and turns the base into passive ingot income."],
    ],
  },
  {
    slug: "palworld-map-region-level-order",
    title: "Palworld Map Region Level Order",
    desc: "The order to clear Palworld's map regions by level band, which region to skip early, and how region choice changes your base and mount plan.",
    keywords: "palworld map region level order, palworld region level requirements, palworld where to go next, palworld map progression route",
    hub: "/map",
    hubLabel: "Palworld map",
    question: "In what order should you clear Palworld's map regions?",
    answer:
      "Follow the level bands outward from your starting region, unlocking a border fast travel statue in each one before you fight it. Filter the interactive map by level to see exactly which region your current squad can clear next.",
    steps: [
      ["Read the map's level filter, not the geography", "Adjacent regions can differ by dozens of levels; the level band decides the order."],
      ["Unlock the border statue before the fight", "A statue at the boundary lets you retreat, restock and return without a long flight."],
      ["Set a base per band", "One base at each band keeps hauling loops short as you push outward."],
      ["Upgrade the mount at every band", "Traversal cost rises with region size; check the mount speed ranking before the next push."],
      ["Slot the tower boss at the end of each band", "Clearing the tower once your squad handles the region is faster than attempting it early."],
    ],
    data: [
      ["Starting band", "Low-level regions built for the first ten levels and the first base."],
      ["Mid bands", "Element-themed regions where type coverage starts to matter more than raw stats."],
      ["Late bands", "High-level regions holding the late-game materials and the hardest field alphas."],
      ["Bridging", "Border statues let you access a band before you are levelled for it."],
      ["Gating", "Tower bosses pace progression between bands."],
    ],
    pitfalls: [
      ["Following geography instead of levels", "Neighbouring regions can be wildly out of band; check the filter first."],
      ["One base for the whole map", "Hauling costs balloon; add a base per band."],
      ["Keeping the starter mount", "Traversal, not combat, becomes the bottleneck in the larger late regions."],
      ["Saving all tower bosses for the end", "They gate progression, so clear each one as its band closes."],
    ],
    faq: [
      ["Where should I go after the first region in Palworld?", "Use the map's level filter and pick the next band up, unlocking its border statue before you commit a squad."],
      ["Are Palworld map regions level-gated?", "Not hard-gated, but the spawn levels are, so entering far above your band means unwinnable fights."],
      ["How many bases do I need across the map?", "One per level band is the practical answer: it keeps hauling and travel loops short as you push outward."],
      ["When should I fight the tower bosses?", "As each band closes, once your squad clears that region's field alphas comfortably."],
    ],
  },
];

const POOL = OFFICIAL_LINKS;
function pool(offset: number) {
  return [POOL[offset % POOL.length], POOL[(offset + 1) % POOL.length], POOL[(offset + 2) % POOL.length]];
}

function articles(t: Topic): GuideArticle[] {
  const hubSentence = `Everything here feeds the ${t.hubLabel} at ${t.hub}, so run the tool alongside the guide.`;
  return [
    {
      title: t.title,
      slug: "overview",
      intro: `${t.answer} ${hubSentence}`,
      sections: [
        { heading: t.question, text: t.answer },
        { heading: "Why this matters", text: `${t.steps[0][1]} ${t.pitfalls[0][1]}` },
        { heading: "What the game actually checks", text: t.data.map(([k, v]) => `${k}: ${v}`).join(" ") },
        { heading: `Use it with the ${t.hubLabel}`, text: `${hubSentence} Open ${t.hub} in a second tab, apply the filters described below, and work from the tool's output instead of memorised combos.` },
      ],
      externalLinks: pool(0),
    },
    {
      title: `${t.title}: step-by-step`,
      slug: "step-by-step",
      intro: `The exact order to follow. Each step removes a failure mode that costs real time in-game.`,
      sections: t.steps.map(([heading, text], i) => ({ heading: `${i + 1}. ${heading}`, text })),
      externalLinks: pool(1),
    },
    {
      title: `${t.title}: reference table`,
      slug: "reference",
      intro: `The values and rules behind ${t.title.toLowerCase()}, in the order you will need them.`,
      sections: t.data.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(2),
    },
    {
      title: `${t.title}: mistakes and fixes`,
      slug: "mistakes",
      intro: `Four failure modes that account for most wasted time on this topic, each with the fix.`,
      sections: t.pitfalls.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(3),
    },
    {
      title: `${t.title}: FAQ`,
      slug: "faq",
      intro: `Direct answers to the questions players ask about ${t.title.toLowerCase()}.`,
      sections: t.faq.map(([q, a]) => ({ heading: q, text: a })),
      externalLinks: pool(0),
    },
  ];
}

export const LONGTAIL_GUIDES: Guide[] = TOPICS.map((t) => ({
  slug: t.slug,
  title: `${t.title} Guide & FAQ`,
  desc: t.desc,
  tag: t.hub === "/breeding" ? "Breeding" : "Maps",
  updated: "2026-08-26",
  keywords: t.keywords,
  articles: articles(t),
}));

/** Hub page each cluster supports, for internal-link rendering. */
export const LONGTAIL_CLUSTERS: Record<string, { slug: string; title: string }[]> = TOPICS.reduce(
  (acc, t) => {
    (acc[t.hub] ??= []).push({ slug: t.slug, title: t.title });
    return acc;
  },
  {} as Record<string, { slug: string; title: string }[]>,
);
